/**
 * CommandsUtils - React 版命令监听工具
 * 类似 Vue 版本的 watchCommands，监听命令队列并执行函数
 */

import { useAndStore } from "./store/useAndStore";

/**
 * 命令项接口
 */
export interface CommandItem {
  actionName: string;
  arg?: any;
  timestamp: number;
}

/**
 * 监听命令队列的通用函数
 * @param _store - store 实例（从 useItemStore 获取）
 * @param sessionId - 会话ID
 * @param context - 执行上下文，包含可调用的函数
 * @returns 取消订阅函数
 */
export function watchCommands(
  _store: any,
  sessionId: string,
  context: Record<string, (...args: any[]) => void> = {}
): () => void {
  // 使用 subscribeWithSelector 监听特定 slice
  // selector: 只选出当前 session 的命令队列
  // listener: 仅当队列引用发生变化时触发
  // @ts-ignore - 忽略 TS 类型检查，因为 useAndStore 类型定义未包含 subscribeWithSelector
  const unsubscribe = (useAndStore as any).subscribe(
    (state: any) => state.commandQueues[sessionId],
    (queue: CommandItem[]) => {
      if (queue && queue.length > 0) {
        const commandsToExecute = [...queue];
        const remaining: CommandItem[] = [];
        let handledCount = 0;

        for (const item of commandsToExecute) {
          const func = context[item.actionName];
          if (typeof func !== "function") {
            // 留给其他订阅方（如 nuwa-report ReportCommandBridge 的 report:* 槽位）
            remaining.push(item);
            continue;
          }
          handledCount += 1;
          try {
            func(item.arg);
          } catch (error) {
            console.error("[watchCommands] 命令执行失败:", item, error);
          }
        }

        if (handledCount > 0) {
          useAndStore.setState((state) => {
            state.commandQueues[sessionId] = remaining;
          });
        }
      }
    }
  );

  return unsubscribe;
}

/**
 * 创建一个绑定了模块上下文的 watchCommands 函数
 * @param getModuleExports - 获取当前模块导出对象的函数（支持延迟获取）
 * @returns 绑定了上下文的 watchCommands 函数
 */
export function createWatchCommands(
  getModuleExports: () => Record<string, (...args: any[]) => void>
) {
  return function (store: any, sessionId: string): () => void {
    // 延迟获取模块导出，避免函数声明顺序问题
    const moduleExports = getModuleExports();
    return watchCommands(store, sessionId, moduleExports);
  };
}
